import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { ArrowBack as BackIcon, Refresh as RefreshIcon, Edit as EditIcon, ExpandMore as ExpandMoreIcon, PersonOutline as PersonIcon, Download as DownloadIcon, Receipt as ReceiptIcon, Description as DescriptionIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { adminApi, Organization, FeatureDefinition, FeatureCategory, OrganizationInvoice } from '../services/api';
import FeatureSelector from '../components/FeatureSelector';

interface MemberInfo {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  isPaid: boolean;
  isEnabled: boolean;
  createdAt: string;
  usage?: {
    submittedCount: number;
    completedCount: number;
  };
}

interface UsageHistoryItem {
  period: string;
  submittedCount: number;
  completedCount: number;
  failedCount: number;
  totalCostUsd?: number;
}

const OrganizationDetailPage: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [members, setMembers] = useState<MemberInfo[]>([]);
  const [memberCount, setMemberCount] = useState<number>(0); // Total member count from API
  const [usageHistory, setUsageHistory] = useState<UsageHistoryItem[]>([]);
  const [allFeatures, setAllFeatures] = useState<FeatureDefinition[]>([]);
  const [featuresByCategory, setFeaturesByCategory] = useState<Record<FeatureCategory, FeatureDefinition[]>>({} as any);
  const [categoryLabels, setCategoryLabels] = useState<Record<FeatureCategory, string>>({} as any);
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [editFeaturesOpen, setEditFeaturesOpen] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [savingFeatures, setSavingFeatures] = useState(false);
  const [featuresError, setFeaturesError] = useState<string | null>(null);
  const [editAccountManagerOpen, setEditAccountManagerOpen] = useState(false);
  const [accountManagerName, setAccountManagerName] = useState('');
  const [accountManagerEmail, setAccountManagerEmail] = useState('');
  const [savingAccountManager, setSavingAccountManager] = useState(false);
  const [accountManagerError, setAccountManagerError] = useState<string | null>(null);
  const [editBillingOpen, setEditBillingOpen] = useState(false);
  const [billingName, setBillingName] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [billingVatNumber, setBillingVatNumber] = useState('');
  const [billingCostPerLicense, setBillingCostPerLicense] = useState('');
  const [savingBilling, setSavingBilling] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [editOrgDetailsOpen, setEditOrgDetailsOpen] = useState(false);
  const [orgDisplayName, setOrgDisplayName] = useState('');
  const [orgDomain, setOrgDomain] = useState('');
  const [orgMaxRuns, setOrgMaxRuns] = useState('');
  const [orgMaxDocChat, setOrgMaxDocChat] = useState('');
  const [orgMaxResearch, setOrgMaxResearch] = useState('');
  const [orgSharedRunPool, setOrgSharedRunPool] = useState(true);
  const [savingOrgDetails, setSavingOrgDetails] = useState(false);
  const [orgDetailsError, setOrgDetailsError] = useState<string | null>(null);
  
  // Invoice state
  const [invoices, setInvoices] = useState<OrganizationInvoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState<string | null>(null);
  
  // Invoice generation modal state
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoiceModalPeriod, setInvoiceModalPeriod] = useState<string>('');
  const [invoiceCustomerName, setInvoiceCustomerName] = useState('');
  const [invoiceCustomerAddress, setInvoiceCustomerAddress] = useState('');
  const [invoiceVatNumber, setInvoiceVatNumber] = useState('');
  const [invoiceDescription, setInvoiceDescription] = useState('');
  const [invoiceNumLicenses, setInvoiceNumLicenses] = useState('');
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [invoiceGenerating, setInvoiceGenerating] = useState(false);
  const [invoiceModalError, setInvoiceModalError] = useState<string | null>(null);
  
  // Job Export state
  const [jobExports, setJobExports] = useState<Array<{
    period: string;
    exists: boolean;
    url: string | null;
    generatedAt: string | null;
    fileName: string | null;
    jobCount: number | null;
  }>>([]);
  const [jobExportsLoading, setJobExportsLoading] = useState(false);
  const [jobExportsError, setJobExportsError] = useState<string | null>(null);
  const [exportingJobPeriod, setExportingJobPeriod] = useState<string | null>(null);
  
  // Delete organization state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Pagination (using DynamoDB LastEvaluatedKey pattern)
  const [hasMoreMembers, setHasMoreMembers] = useState(false);
  const [membersNextKey, setMembersNextKey] = useState<string | undefined>();

  // Fetch organization details
  useEffect(() => {
    const fetchOrgDetails = async () => {
      if (!orgId) return;

      try {
        setLoading(true);
        const [orgResponse, featuresResponse] = await Promise.all([
          adminApi.getOrganization(orgId),
          adminApi.listFeatures()
        ]);
        
        if (orgResponse.success) {
          setOrganization(orgResponse.data.organization);
          setUsageHistory(orgResponse.data.usageHistory || []);
          setMemberCount(orgResponse.data.memberCount || 0); // Set total member count
        }

        if (featuresResponse.success) {
          setAllFeatures(featuresResponse.data.features);
          setFeaturesByCategory(featuresResponse.data.byCategory);
          setCategoryLabels(featuresResponse.data.categoryLabels);
        }
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load organization');
      } finally {
        setLoading(false);
      }
    };

    fetchOrgDetails();
  }, [orgId]);

  // Current month usage is the first item in the history
  const currentUsage = usageHistory.length > 0 ? usageHistory[0] : null;

  // Fetch members from DynamoDB user-profiles table via GSI
  const fetchMembers = useCallback(async (lastKey?: string) => {
    if (!orgId) return;

    try {
      setMembersLoading(true);
      setMembersError(null);
      
      const response = await adminApi.listOrganizationMembers(orgId, {
        limit: 50,
        lastKey,
        includeUsage: true
      });
      
      if (response.success) {
        if (lastKey) {
          // Append to existing members (pagination)
          setMembers(prev => [...prev, ...response.data.members]);
        } else {
          // Replace members (initial load or refresh)
          setMembers(response.data.members);
        }
        setHasMoreMembers(response.data.pagination.hasMore);
        setMembersNextKey(response.data.pagination.nextKey);
      }
    } catch (err: any) {
      setMembersError(err.response?.data?.message || err.message || 'Failed to load members');
    } finally {
      setMembersLoading(false);
    }
  }, [orgId]);

  // Fetch members on mount
  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    if (!orgId) return;

    try {
      setInvoicesLoading(true);
      setInvoicesError(null);
      
      const response = await adminApi.listOrganizationInvoices(orgId);
      
      if (response.success) {
        setInvoices(response.data.invoices);
      }
    } catch (err: any) {
      setInvoicesError(err.response?.data?.message || err.message || 'Failed to load invoices');
    } finally {
      setInvoicesLoading(false);
    }
  }, [orgId]);

  // Fetch invoices on mount
  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Fetch job exports (check S3 for existing exports)
  const fetchJobExports = useCallback(async () => {
    if (!orgId) return;

    try {
      setJobExportsLoading(true);
      setJobExportsError(null);
      
      const response = await adminApi.listOrganizationJobExports(orgId);
      
      if (response.success) {
        setJobExports(response.data.exports);
      }
    } catch (err: any) {
      setJobExportsError(err.response?.data?.message || err.message || 'Failed to load job exports');
    } finally {
      setJobExportsLoading(false);
    }
  }, [orgId]);

  // Fetch job exports when mounted or when orgId changes
  useEffect(() => {
    fetchJobExports();
  }, [fetchJobExports]);

  const handleGenerateInvoice = async (period: string) => {
    if (!orgId || !organization) return;

    // Get the usage data for this period
    const periodUsage = usageHistory.find(u => u.period === period);
    const totalCost = periodUsage?.totalCostUsd || 0;

    // Calculate amount: use cost per license if set, otherwise use usage cost
    let calculatedAmount = totalCost;
    if (organization.costPerLicenseZAR && memberCount > 0) {
      // Use license-based pricing in ZAR
      calculatedAmount = organization.costPerLicenseZAR * memberCount;
    }

    // Format period for description
    const [year, month] = period.split('-');
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
    const monthName = monthNames[parseInt(month) - 1];
    const description = organization.costPerLicenseZAR 
      ? `Monthly license fees for ${monthName} ${year} (${memberCount} licenses @ R${organization.costPerLicenseZAR.toFixed(2)} per license)`
      : `AI usage for ${monthName} ${year}`;

    // Set modal state
    setInvoiceModalPeriod(period);
    setInvoiceCustomerName(organization.billingName || organization.displayName || organization.name);
    setInvoiceCustomerAddress(organization.billingAddress || '');
    setInvoiceVatNumber(organization.vatNumber || '');
    setInvoiceDescription(description);
    setInvoiceNumLicenses(String(memberCount));
    setInvoiceAmount(String(Math.round(calculatedAmount)));
    setInvoiceModalError(null);
    setInvoiceModalOpen(true);
  };

  const handleExportJobs = async (period: string) => {
    if (!orgId) return;
    
    try {
      setExportingJobPeriod(period);
      setJobExportsError(null);
      
      const response = await adminApi.exportOrganizationJobs(orgId, period);
      
      if (response.success && response.data?.downloadUrl) {
        // Open download in new tab
        window.open(response.data.downloadUrl, '_blank');
        
        // Refresh the exports list to show the newly generated export
        await fetchJobExports();
      } else {
        throw new Error('No download URL returned');
      }
    } catch (err: any) {
      setJobExportsError(err.response?.data?.message || err.message || 'Failed to export jobs');
    } finally {
      setExportingJobPeriod(null);
    }
  };

  const handleConfirmGenerateInvoice = async () => {
    if (!orgId) return;

    // Validate required fields
    if (!invoiceCustomerName.trim()) {
      setInvoiceModalError('Customer name is required');
      return;
    }
    if (!invoiceCustomerAddress.trim()) {
      setInvoiceModalError('Customer address is required');
      return;
    }
    
    const numLicenses = parseInt(invoiceNumLicenses);
    const amount = parseFloat(invoiceAmount);
    
    if (isNaN(numLicenses) || numLicenses < 0) {
      setInvoiceModalError('Number of licenses must be a valid number');
      return;
    }
    if (isNaN(amount) || amount < 0) {
      setInvoiceModalError('Amount must be a valid number');
      return;
    }

    try {
      setInvoiceGenerating(true);
      setInvoiceModalError(null);
      
      // Send custom invoice data to backend
      const response = await adminApi.generateOrganizationInvoice(orgId, invoiceModalPeriod, {
        customerName: invoiceCustomerName.trim(),
        customerAddress: invoiceCustomerAddress.trim(),
        vatNumber: invoiceVatNumber.trim(),
        description: invoiceDescription.trim(),
        numLicenses: numLicenses,
        amountZar: amount
      });
      
      if (response.success && response.data.url) {
        // Open download in new tab
        window.open(response.data.url, '_blank');
        
        // Refresh invoice list
        await fetchInvoices();
        
        // Close modal
        setInvoiceModalOpen(false);
      }
    } catch (err: any) {
      setInvoiceModalError(err.response?.data?.message || err.message || 'Failed to generate invoice');
    } finally {
      setInvoiceGenerating(false);
    }
  };

  const formatPeriod = (period: string): string => {
    const [year, month] = period.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const isCurrentMonth = (period: string): boolean => {
    const now = new Date();
    const currentPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return period === currentPeriod;
  };

  const handleRefreshMembers = () => {
    setMembersNextKey(undefined);
    fetchMembers();
  };

  const handleLoadMoreMembers = () => {
    if (membersNextKey) {
      fetchMembers(membersNextKey);
    }
  };

  const handleEditFeaturesClick = () => {
    setSelectedFeatures(organization?.enabledFeatures || organization?.features || []);
    setFeaturesError(null);
    setEditFeaturesOpen(true);
  };

  const handleEditAccountManagerClick = () => {
    setAccountManagerName(organization?.accountManagerName || '');
    setAccountManagerEmail(organization?.accountManagerEmail || '');
    setAccountManagerError(null);
    setEditAccountManagerOpen(true);
  };

  const handleEditBillingClick = () => {
    setBillingName(organization?.billingName || '');
    setBillingAddress(organization?.billingAddress || '');
    setBillingVatNumber(organization?.vatNumber || '');
    setBillingCostPerLicense(String(organization?.costPerLicenseZAR || ''));
    setBillingError(null);
    setEditBillingOpen(true);
  };

  const handleEditOrgDetailsClick = () => {
    setOrgDisplayName(organization?.displayName || '');
    setOrgDomain(organization?.domain || '');
    setOrgMaxRuns(String(organization?.maxRunsPerMonth || 50));
    setOrgMaxDocChat(String(organization?.maxDocChatPerMonth || 50));
    setOrgMaxResearch(String(organization?.maxResearchPerMonth || 50));
    setOrgSharedRunPool(organization?.sharedRunPool !== false);
    setOrgDetailsError(null);
    setEditOrgDetailsOpen(true);
  };

  const handleSaveAccountManager = async () => {
    if (!orgId) return;

    // Validate email format if provided
    if (accountManagerEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(accountManagerEmail.trim())) {
      setAccountManagerError('Invalid email format');
      return;
    }

    try {
      setSavingAccountManager(true);
      setAccountManagerError(null);
      
      await adminApi.updateOrganization(orgId, {
        accountManagerName: accountManagerName.trim() || undefined,
        accountManagerEmail: accountManagerEmail.trim() || undefined
      });

      // Refresh organization data
      const response = await adminApi.getOrganization(orgId);
      if (response.success) {
        setOrganization(response.data.organization);
      }

      setEditAccountManagerOpen(false);
    } catch (err: any) {
      setAccountManagerError(err.response?.data?.message || err.message || 'Failed to update account manager');
    } finally {
      setSavingAccountManager(false);
    }
  };

  const handleSaveBilling = async () => {
    if (!orgId) return;

    // Validate cost per license if provided
    const costPerLicense = billingCostPerLicense.trim() ? parseFloat(billingCostPerLicense) : undefined;
    if (billingCostPerLicense.trim() && (isNaN(costPerLicense!) || costPerLicense! < 0)) {
      setBillingError('Cost per license must be a valid positive number');
      return;
    }

    try {
      setSavingBilling(true);
      setBillingError(null);
      
      await adminApi.updateOrganization(orgId, {
        billingName: billingName.trim() || undefined,
        billingAddress: billingAddress.trim() || undefined,
        vatNumber: billingVatNumber.trim() || undefined,
        costPerLicenseZAR: costPerLicense
      });

      // Refresh organization data
      const response = await adminApi.getOrganization(orgId);
      if (response.success) {
        setOrganization(response.data.organization);
      }

      setEditBillingOpen(false);
    } catch (err: any) {
      setBillingError(err.response?.data?.message || err.message || 'Failed to update billing information');
    } finally {
      setSavingBilling(false);
    }
  };

  const handleSaveOrgDetails = async () => {
    if (!orgId) return;

    // Validate
    const maxRuns = parseInt(orgMaxRuns);
    const maxDocChat = parseInt(orgMaxDocChat);
    const maxResearch = parseInt(orgMaxResearch);
    
    if (isNaN(maxRuns) || maxRuns < 0) {
      setOrgDetailsError('Monthly run limit must be a positive number');
      return;
    }
    if (isNaN(maxDocChat) || maxDocChat < 0) {
      setOrgDetailsError('Document chat limit must be a positive number');
      return;
    }
    if (isNaN(maxResearch) || maxResearch < 0) {
      setOrgDetailsError('Legal research limit must be a positive number');
      return;
    }

    try {
      setSavingOrgDetails(true);
      setOrgDetailsError(null);
      
      await adminApi.updateOrganization(orgId, {
        displayName: orgDisplayName.trim() || undefined,
        domain: orgDomain.trim() || undefined,
        maxRunsPerMonth: maxRuns,
        maxDocChatPerMonth: maxDocChat,
        maxResearchPerMonth: maxResearch,
        sharedRunPool: orgSharedRunPool,
      });

      // Refresh organization data
      const response = await adminApi.getOrganization(orgId);
      if (response.success) {
        setOrganization(response.data.organization);
      }

      setEditOrgDetailsOpen(false);
    } catch (err: any) {
      setOrgDetailsError(err.response?.data?.message || err.message || 'Failed to update organization details');
    } finally {
      setSavingOrgDetails(false);
    }
  };

  const handleSaveFeatures = async () => {
    if (!orgId) return;

    try {
      setSavingFeatures(true);
      setFeaturesError(null);
      
      await adminApi.updateOrganization(orgId, {
        enabledFeatures: selectedFeatures
      });

      // Refresh organization data
      const response = await adminApi.getOrganization(orgId);
      if (response.success) {
        setOrganization(response.data.organization);
      }

      setEditFeaturesOpen(false);
    } catch (err: any) {
      setFeaturesError(err.response?.data?.message || err.message || 'Failed to update features');
    } finally {
      setSavingFeatures(false);
    }
  };

  const handleDeleteOrganization = async () => {
    if (!orgId) return;

    try {
      setDeleting(true);
      setDeleteError(null);

      await adminApi.deleteOrganization(orgId);

      // Navigate back to organizations list on success
      navigate('/organizations');
    } catch (err: any) {
      setDeleteError(err.response?.data?.message || err.message || 'Failed to delete organization');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/organizations')} sx={{ mb: 2 }}>
          Back to Organizations
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!organization) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/organizations')} sx={{ mb: 2 }}>
          Back to Organizations
        </Button>
        <Alert severity="warning">Organization not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/organizations')} sx={{ mb: 3 }}>
        Back to Organizations
      </Button>

      {/* Organization Info Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" fontWeight={700}>
              {organization.displayName || organization.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={handleEditOrgDetailsClick}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => { setDeleteConfirmName(''); setDeleteError(null); setDeleteDialogOpen(true); }}
              >
                Delete
              </Button>
            </Box>
          </Box>
          
          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">ID</Typography>
              <Typography sx={{ fontFamily: 'monospace' }}>{organization.id}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Name</Typography>
              <Typography>{organization.name}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Domain</Typography>
              <Typography>{organization.domain || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Monthly Run Limit</Typography>
              <Typography>{organization.maxRunsPerMonth || 50}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Doc Chat Limit</Typography>
              <Typography>{organization.maxDocChatPerMonth || 50}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Research Limit</Typography>
              <Typography>{organization.maxResearchPerMonth || 50}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Run Pool Mode</Typography>
              <Chip
                label={organization.sharedRunPool !== false ? 'Shared Pool' : 'Per-User'}
                color={organization.sharedRunPool !== false ? 'primary' : 'default'}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Account Manager Section */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Account Manager
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={handleEditAccountManagerClick}
            >
              Edit
            </Button>
          </Box>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">Name</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <PersonIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                <Typography>{organization.accountManagerName || '-'}</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">Email</Typography>
              <Typography sx={{ mt: 0.5 }}>
                {organization.accountManagerEmail ? (
                  <a href={`mailto:${organization.accountManagerEmail}`} style={{ color: 'inherit' }}>
                    {organization.accountManagerEmail}
                  </a>
                ) : '-'}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Billing Information Section */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Billing Information
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={handleEditBillingClick}
            >
              Edit
            </Button>
          </Box>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">Billing Name</Typography>
              <Typography sx={{ mt: 0.5 }}>{organization.billingName || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">VAT Number</Typography>
              <Typography sx={{ mt: 0.5 }}>{organization.vatNumber || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">Number of Licenses</Typography>
              <Typography sx={{ mt: 0.5, fontWeight: 600 }}>
                {memberCount > 0 ? `${memberCount} ${memberCount === 1 ? 'user' : 'users'}` : '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">Cost per License (ZAR)</Typography>
              <Typography sx={{ mt: 0.5 }}>
                {organization.costPerLicenseZAR ? `R ${organization.costPerLicenseZAR.toFixed(2)}` : '-'}
              </Typography>
            </Grid>
            {organization.costPerLicenseZAR && memberCount > 0 && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">Monthly Total (ZAR)</Typography>
                <Typography sx={{ mt: 0.5, fontWeight: 600, color: 'primary.main' }}>
                  R {(organization.costPerLicenseZAR * memberCount).toFixed(2)}
                </Typography>
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <Typography variant="caption" color="text.secondary">Billing Address</Typography>
              <Typography sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
                {organization.billingAddress || '-'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Usage Stats and History Chart */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Usage & Cost History (Last 3 Months)
              </Typography>
              <Box sx={{ height: 250, mt: 2 }}>
                {usageHistory.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[...usageHistory].reverse()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis yAxisId="left" orientation="left" stroke="#3182ce" />
                      <YAxis yAxisId="right" orientation="right" stroke="#e53e3e" />
                      <Tooltip 
                        formatter={(value: any, name: string) => {
                          if (name === 'Total Cost (USD)') return [`$${Number(value).toFixed(2)}`, name];
                          return [value, name];
                        }}
                      />
                      <Bar yAxisId="left" dataKey="completedCount" name="Completed Jobs" fill="#3182ce" radius={[4, 4, 0, 0]} />
                      <Bar yAxisId="right" dataKey="totalCostUsd" name="Total Cost (USD)" fill="#e53e3e" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography color="text.secondary">No usage data</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Current Period Stats
              </Typography>
              {currentUsage ? (
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      ESTIMATED COST
                    </Typography>
                    <Typography variant="h4" color="primary.main" fontWeight={700}>
                      ${(currentUsage.totalCostUsd || 0).toFixed(2)}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Submitted</Typography>
                      <Typography variant="h6">{currentUsage.submittedCount}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Completed</Typography>
                      <Typography variant="h6" color="success.main">{currentUsage.completedCount}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Failed</Typography>
                      <Typography variant="h6" color="error.main">{currentUsage.failedCount}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Period</Typography>
                      <Typography variant="body1" fontWeight={500}>{currentUsage.period}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', py: 4 }}>
                  <Typography color="text.secondary">No current period data</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Features Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Features Configuration
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(organization?.enabledFeatures || organization?.features || []).length} of {allFeatures.length} features enabled
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEditFeaturesClick}
            >
              Edit Features
            </Button>
          </Box>

          {Object.entries(featuresByCategory).map(([category, features]) => {
            const categoryKey = category as FeatureCategory;
            const enabledFeatureIds = new Set(organization?.enabledFeatures || organization?.features || []);
            const enabledCount = features.filter(f => enabledFeatureIds.has(String(f.id))).length;

            return (
              <Accordion key={category}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {categoryLabels[categoryKey] || category}
                    </Typography>
                    <Chip
                      label={`${enabledCount}/${features.length}`}
                      size="small"
                      color={enabledCount === features.length ? 'success' : enabledCount > 0 ? 'primary' : 'default'}
                      variant="outlined"
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {features.map((feature) => {
                      const isEnabled = enabledFeatureIds.has(String(feature.id));
                      return (
                        <Box
                          key={String(feature.id)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: isEnabled ? 'success.50' : 'grey.50',
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {feature.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {feature.description}
                            </Typography>
                          </Box>
                          <Chip
                            label={isEnabled ? 'Enabled' : 'Disabled'}
                            size="small"
                            color={isEnabled ? 'success' : 'default'}
                            variant={isEnabled ? 'filled' : 'outlined'}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </CardContent>
      </Card>

      {/* Job History Export */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Job History Export
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Last 5 months
              </Typography>
            </Box>
            <Button 
              size="small" 
              startIcon={<RefreshIcon />}
              onClick={fetchJobExports}
              disabled={jobExportsLoading}
            >
              Refresh
            </Button>
          </Box>

          {jobExportsError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setJobExportsError(null)}>
              {jobExportsError}
            </Alert>
          )}

          {jobExportsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={40} />
            </Box>
          ) : jobExports.length === 0 ? (
            <Alert severity="info">No usage history available to generate exports</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Period</strong></TableCell>
                    <TableCell><strong>Export Number</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Generated</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobExports.map((exportItem) => {
                    const [year, month] = exportItem.period.split('-');
                    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                                       'July', 'August', 'September', 'October', 'November', 'December'];
                    const monthName = monthNames[parseInt(month) - 1];
                    const exportNumber = `JOB-${year}-${month}`;
                    const isGenerating = exportingJobPeriod === exportItem.period;

                    return (
                      <TableRow key={exportItem.period}>
                        <TableCell>
                          {monthName} {year}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionIcon fontSize="small" color="action" />
                            {exportNumber}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={exportItem.exists ? 'Generated' : 'Not Generated'}
                            color={exportItem.exists ? 'success' : 'default'}
                            size="small"
                            variant={exportItem.exists ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>
                          {exportItem.generatedAt 
                            ? new Date(exportItem.generatedAt).toLocaleString()
                            : '-'
                          }
                        </TableCell>
                        <TableCell align="right">
                          {exportItem.exists && exportItem.url ? (
                            <>
                              <Button
                                size="small"
                                variant="text"
                                startIcon={<DownloadIcon />}
                                onClick={() => window.open(exportItem.url!, '_blank')}
                                sx={{ mr: 1 }}
                              >
                                Download
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleExportJobs(exportItem.period)}
                                disabled={isGenerating}
                                startIcon={isGenerating ? <CircularProgress size={16} /> : undefined}
                              >
                                {isGenerating ? 'Regenerating...' : 'Regenerate'}
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleExportJobs(exportItem.period)}
                              disabled={isGenerating}
                              startIcon={isGenerating ? <CircularProgress size={16} /> : undefined}
                            >
                              {isGenerating ? 'Generating...' : 'Generate'}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Monthly Invoices */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Monthly Invoices
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Last 5 months
              </Typography>
            </Box>
            <Button 
              size="small" 
              startIcon={<RefreshIcon />}
              onClick={fetchInvoices}
              disabled={invoicesLoading}
            >
              Refresh
            </Button>
          </Box>

          {invoicesError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setInvoicesError(null)}>
              {invoicesError}
            </Alert>
          )}

          {invoicesLoading && invoices.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : invoices.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Period</TableCell>
                    <TableCell>Invoice Number</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Generated</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.period} hover>
                      <TableCell>{formatPeriod(invoice.period)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ReceiptIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {invoice.invoiceNumber}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={invoice.exists ? 'Generated' : 'Not Generated'} 
                          size="small" 
                          color={invoice.exists ? 'success' : 'default'}
                          variant={invoice.exists ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        {invoice.generatedAt ? (
                          <Typography variant="caption">
                            {new Date(invoice.generatedAt).toLocaleDateString()} {new Date(invoice.generatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        ) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          {invoice.exists && invoice.url && (
                            <Button
                              size="small"
                              startIcon={<DownloadIcon />}
                              onClick={() => window.open(invoice.url!, '_blank')}
                            >
                              Download
                            </Button>
                          )}
                          {isCurrentMonth(invoice.period) && (
                            <Button
                              size="small"
                              variant={invoice.exists ? 'outlined' : 'contained'}
                              onClick={() => handleGenerateInvoice(invoice.period)}
                              disabled={invoiceGenerating}
                            >
                              {invoice.exists ? 'Regenerate' : 'Generate'}
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary">No invoice data available</Typography>
          )}
        </CardContent>
      </Card>

      {/* Members */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Members
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button 
                size="small" 
                startIcon={<RefreshIcon />}
                onClick={handleRefreshMembers}
                disabled={membersLoading}
              >
                Refresh
              </Button>
              <Chip label={`${members.length} members`} size="small" />
            </Box>
          </Box>

          {membersError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMembersError(null)}>
              {membersError}
            </Alert>
          )}

          {membersLoading && members.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : members.length > 0 ? (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Paid</TableCell>
                      <TableCell>Jobs (Month)</TableCell>
                      <TableCell>Joined</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {members.map((member) => (
                      <TableRow 
                        key={member.userId}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/users/${member.userId}`)}
                      >
                        <TableCell>{member.email}</TableCell>
                        <TableCell>
                          {member.firstName || member.lastName 
                            ? `${member.firstName || ''} ${member.lastName || ''}`.trim()
                            : '-'
                          }
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={member.isEnabled ? 'Active' : 'Disabled'} 
                            size="small" 
                            color={member.isEnabled ? 'success' : 'error'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={member.isPaid ? 'Yes' : 'No'} 
                            size="small" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {member.usage ? member.usage.submittedCount : '-'}
                        </TableCell>
                        <TableCell>{new Date(member.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Button 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/users/${member.userId}`);
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {hasMoreMembers && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button 
                    onClick={handleLoadMoreMembers} 
                    disabled={membersLoading}
                  >
                    {membersLoading ? <CircularProgress size={20} /> : 'Load More Members'}
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Typography color="text.secondary">No members found</Typography>
          )}
        </CardContent>
      </Card>

      {/* Edit Features Dialog */}
      <Dialog
        open={editFeaturesOpen}
        onClose={() => !savingFeatures && setEditFeaturesOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Organization Features</DialogTitle>
        <DialogContent>
          {featuresError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFeaturesError(null)}>
              {featuresError}
            </Alert>
          )}
          <FeatureSelector
            selectedFeatures={selectedFeatures}
            onChange={setSelectedFeatures}
            disabled={savingFeatures}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditFeaturesOpen(false)} disabled={savingFeatures}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveFeatures}
            disabled={savingFeatures}
            startIcon={savingFeatures ? <CircularProgress size={20} /> : null}
          >
            {savingFeatures ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Account Manager Dialog */}
      <Dialog
        open={editAccountManagerOpen}
        onClose={() => !savingAccountManager && setEditAccountManagerOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Account Manager</DialogTitle>
        <DialogContent>
          {accountManagerError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {accountManagerError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Account Manager Name"
              value={accountManagerName}
              onChange={(e) => setAccountManagerName(e.target.value)}
              fullWidth
              placeholder="e.g., John Smith"
              helperText="Optional: The name of the person managing this account"
            />
            <TextField
              label="Account Manager Email"
              type="email"
              value={accountManagerEmail}
              onChange={(e) => setAccountManagerEmail(e.target.value)}
              fullWidth
              placeholder="e.g., john.smith@example.com"
              helperText="Optional: Email address for support inquiries"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditAccountManagerOpen(false)} disabled={savingAccountManager}>
            Cancel
          </Button>
          <Button onClick={handleSaveAccountManager} variant="contained" disabled={savingAccountManager}>
            {savingAccountManager ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Billing Information Dialog */}
      <Dialog
        open={editBillingOpen}
        onClose={() => !savingBilling && setEditBillingOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Billing Information</DialogTitle>
        <DialogContent>
          {billingError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {billingError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Billing Name"
              value={billingName}
              onChange={(e) => setBillingName(e.target.value)}
              fullWidth
              placeholder="e.g., Intelligent Automation (Pty) Ltd"
              helperText="Name to appear on invoices"
            />
            <TextField
              label="Billing Address"
              value={billingAddress}
              onChange={(e) => setBillingAddress(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="e.g., 43 Blaauwberg Road, Table View, 7441"
              helperText="Physical address for invoicing"
            />
            <TextField
              label="VAT Number"
              value={billingVatNumber}
              onChange={(e) => setBillingVatNumber(e.target.value)}
              fullWidth
              placeholder="e.g., 4123456789"
              helperText="Optional: VAT/Tax registration number"
            />
            <TextField
              label="Cost per License (ZAR)"
              type="number"
              value={billingCostPerLicense}
              onChange={(e) => setBillingCostPerLicense(e.target.value)}
              fullWidth
              placeholder="e.g., 250.00"
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Optional: Monthly cost per user license in South African Rand"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditBillingOpen(false)} disabled={savingBilling}>
            Cancel
          </Button>
          <Button onClick={handleSaveBilling} variant="contained" disabled={savingBilling}>
            {savingBilling ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Organization Details Dialog */}
      <Dialog
        open={editOrgDetailsOpen}
        onClose={() => !savingOrgDetails && setEditOrgDetailsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Organization Details</DialogTitle>
        <DialogContent>
          {orgDetailsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {orgDetailsError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Display Name"
              value={orgDisplayName}
              onChange={(e) => setOrgDisplayName(e.target.value)}
              fullWidth
              placeholder="e.g., Intelligent Automation"
              helperText="Optional: A friendly display name"
            />
            <TextField
              label="Domain"
              value={orgDomain}
              onChange={(e) => setOrgDomain(e.target.value)}
              fullWidth
              placeholder="e.g., intelligent-automation.net"
              helperText="Optional: Primary domain for this organization"
            />
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={orgSharedRunPool}
                    onChange={(e) => setOrgSharedRunPool(e.target.checked)}
                  />
                }
                label="Shared Run Pool"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                When enabled, all members share the organization's monthly run limit.
                When disabled, each member uses their own individual allocation.
              </Typography>
            </Box>
            <TextField
              label="Monthly Run Limit"
              type="number"
              value={orgMaxRuns}
              onChange={(e) => setOrgMaxRuns(e.target.value)}
              fullWidth
              required
              inputProps={{ min: 0, step: 1 }}
              helperText="Maximum number of document processing runs per month"
            />
            <TextField
              label="Document Chat Limit"
              type="number"
              value={orgMaxDocChat}
              onChange={(e) => setOrgMaxDocChat(e.target.value)}
              fullWidth
              required
              inputProps={{ min: 0, step: 1 }}
              helperText="Maximum number of document chat queries per month"
            />
            <TextField
              label="Legal Research Limit"
              type="number"
              value={orgMaxResearch}
              onChange={(e) => setOrgMaxResearch(e.target.value)}
              fullWidth
              required
              inputProps={{ min: 0, step: 1 }}
              helperText="Maximum number of legal research (textbook) queries per month"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOrgDetailsOpen(false)} disabled={savingOrgDetails}>
            Cancel
          </Button>
          <Button onClick={handleSaveOrgDetails} variant="contained" disabled={savingOrgDetails}>
            {savingOrgDetails ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Generation Confirmation Modal */}
      <Dialog
        open={invoiceModalOpen}
        onClose={() => !invoiceGenerating && setInvoiceModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Generate Invoice - {formatPeriod(invoiceModalPeriod)}
        </DialogTitle>
        <DialogContent>
          {invoiceModalError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setInvoiceModalError(null)}>
              {invoiceModalError}
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            Review and edit the invoice details below before generating.
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Customer Name"
              value={invoiceCustomerName}
              onChange={(e) => setInvoiceCustomerName(e.target.value)}
              fullWidth
              required
              placeholder="e.g., Intelligent Automation (Pty) Ltd"
              helperText="Name to appear on invoice"
            />
            <TextField
              label="Customer Address"
              value={invoiceCustomerAddress}
              onChange={(e) => setInvoiceCustomerAddress(e.target.value)}
              fullWidth
              required
              multiline
              rows={3}
              placeholder="e.g., 43 Blaauwberg Road, Table View, 7441"
              helperText="Physical address for invoice"
            />
            <TextField
              label="VAT Number"
              value={invoiceVatNumber}
              onChange={(e) => setInvoiceVatNumber(e.target.value)}
              fullWidth
              placeholder="e.g., 4123456789"
              helperText="Optional: VAT/Tax registration number"
            />
            <Divider sx={{ my: 1 }} />
            <TextField
              label="Description"
              value={invoiceDescription}
              onChange={(e) => setInvoiceDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              helperText="Service description for invoice"
            />
            <TextField
              label="Number of Licenses"
              type="number"
              value={invoiceNumLicenses}
              onChange={(e) => setInvoiceNumLicenses(e.target.value)}
              fullWidth
              required
              inputProps={{ min: 0, step: 1 }}
              helperText="Based on current member count"
            />
            <TextField
              label={organization?.costPerLicenseZAR ? "Amount (ZAR)" : "Amount (USD)"}
              type="number"
              value={invoiceAmount}
              onChange={(e) => setInvoiceAmount(e.target.value)}
              fullWidth
              required
              inputProps={{ min: 0, step: 0.01 }}
              helperText={organization?.costPerLicenseZAR 
                ? `Total: ${invoiceNumLicenses} licenses × R${organization.costPerLicenseZAR.toFixed(2)} = R${invoiceAmount}`
                : "Total cost for the period (from usage data)"}
            />
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                INVOICE NUMBER
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                INV-{invoiceModalPeriod}-{organization?.name.toLowerCase().replace(/\s+/g, '-') || 'org'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceModalOpen(false)} disabled={invoiceGenerating}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmGenerateInvoice} 
            variant="contained" 
            disabled={invoiceGenerating}
            startIcon={invoiceGenerating ? <CircularProgress size={20} /> : null}
          >
            {invoiceGenerating ? 'Generating...' : 'Generate Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Organization Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Delete Organization</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError(null)}>
              {deleteError}
            </Alert>
          )}
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will soft-delete the organization, revoke all its API keys, and remove its domain entries.
            <br /><br />
            <strong>The organization must have zero members.</strong> Remove or reassign all members before deleting.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            To confirm deletion, please type the organization name below:
          </Typography>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 2, fontFamily: 'monospace' }}>
            {organization?.name}
          </Typography>
          <TextField
            fullWidth
            label="Confirm Organization Name"
            value={deleteConfirmName}
            onChange={(e) => setDeleteConfirmName(e.target.value)}
            placeholder={organization?.name}
            disabled={deleting}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteOrganization}
            disabled={deleting || deleteConfirmName !== organization?.name}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete Organization'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizationDetailPage;
